import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModuleFilter } from '@/context/ModuleFilter';
import { useDailyReports } from '@/services/Reports';

import { DATE_RANGES, DEFAULT_REPORT_RANGE, RANGE_LABEL_KEY } from './Quality.constants';
import { DailyReportsTable } from './Quality.DailyReportsTable';
import { DailyReportsLoading } from './Quality.loading';
import { ReportDistribution, ReportScoreCards } from './Quality.ReportCards';
import { styles } from './Quality.styles';
import type { DateRange, ModuleReportProps, ReportRangeToggleProps } from './Quality.types';
import { computeReportRange, groupByModule } from './Quality.utils';

const ReportRangeToggle = ({ value, onChange }: ReportRangeToggleProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });

	return (
		<div className={styles.reportsRangeGroup}>
			{DATE_RANGES.map((range) => (
				<button
					key={range}
					type="button"
					aria-pressed={range === value}
					onClick={() => onChange(range)}
					className={`${styles.reportsRangeBtn} ${range === value ? styles.reportsRangeBtnActive : styles.reportsRangeBtnIdle}`}
				>
					{t(RANGE_LABEL_KEY[range])}
				</button>
			))}
		</div>
	);
};

const ModuleReport = ({ moduleName, rows, expandedKey, onToggle }: ModuleReportProps) => {
	const latest = rows[0];

	return (
		<section className={styles.moduleReport}>
			<h3 className={styles.moduleReportTitle}>{moduleName}</h3>
			<ReportScoreCards latest={latest} />
			{latest && latest.externalTotal > 0 && <ReportDistribution report={latest} />}
			<DailyReportsTable rows={rows} expandedKey={expandedKey} onToggle={onToggle} />
		</section>
	);
};

/** Daily LLM-judge and customer-satisfaction reports per module, shown as a section of the Quality page. */
export const DailyReports = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });
	const { selectedModule } = useModuleFilter();
	const [range, setRange] = useState<DateRange>(DEFAULT_REPORT_RANGE);
	const [expandedKey, setExpandedKey] = useState<string | null>(null);
	// No `includeConsolidated`: the API caps consolidated ranges at 7 rows and bills
	// an LLM call per row, so each row fetches its own summary when expanded.
	const params = useMemo(() => ({ ...computeReportRange(range), moduleName: selectedModule }), [range, selectedModule]);
	const { data, isLoading, isError } = useDailyReports(params);
	const toggle = (key: string) => setExpandedKey((current) => (current === key ? null : key));

	if (isLoading) return <DailyReportsLoading />;
	if (isError || !data) return <p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>;

	const grouped = groupByModule(data.items);

	return (
		<>
			<div className={styles.reportsToolbar}>
				<p className={styles.pageSub}>{t('SUBTITLE')}</p>
				<ReportRangeToggle value={range} onChange={setRange} />
			</div>
			{grouped.size === 0 ? (
				<section className={styles.reportsEmpty}>
					<p className={styles.reportsEmptyTitle}>{t('EMPTY_TITLE')}</p>
					<p className={styles.reportsEmptyBody}>{t('EMPTY_BODY')}</p>
				</section>
			) : (
				Array.from(grouped, ([moduleName, rows]) => (
					<ModuleReport
						key={moduleName}
						moduleName={moduleName}
						rows={rows}
						expandedKey={expandedKey}
						onToggle={toggle}
					/>
				))
			)}
		</>
	);
};
